import * as React from 'react'
import * as R from 'ramda'
import yup from 'yup'
// import * as yup from 'yup';
import { Dispatch } from 'react-redux'
import { Field, reduxForm, InjectedFormProps } from 'redux-form'
import { asyncValidate, shouldAsyncValidate } from 'redux-form-yup'

import { UnitInput, UnitConfig, TextInput, FieldConfig } from '../Inputs'
import { updateLoad } from '../../../../actions/project'
import { Store } from '../../../../reducers'
import { parseFloats } from './shared'
import CustomSelect, { SelectProps } from '../../../Shared/CustomSelect'
import {
  unitConfigConversion,
  parseKNm2,
  formatKNm2,
  _parseKNm,
  _formatKNm,
  // formatKNm,
  formatMm,
  parseMm,
  parseKN,
  formatKN
} from '../../../../location_changes/conversionHelper'

type FormProps = {
  loadCaseId: string
  loadIndex: number

  auto?: boolean
  autoValue?: boolean
  maxLength?: number
}

type AreaLoadProps = {
  name: string
  value: number
  type: string
}

export const AreaLoadSchema = yup.object().shape({
  name: yup
    .string()
    .min(1)
    .required(),
  type: yup.string().required(),
  value: yup.lazy(() => {
    return yup
      .number()
      .required()
      .min(-500, 'Must be greater than or equal to -' + formatKNm2(500))
      .max(500, 'Must be less than or equal to ' + formatKNm2(500))
  })
})

const options = [
  { value: 'area', displayName: 'Area load' },
  { value: 'point', displayName: 'Point load' },
  { value: 'line', displayName: 'Line load' }
]

const parseOptions = () => {
  return options.map((option, i) => (
    <option key={i} value={option.value}>
      {option.displayName}
    </option>
  ))
}

export const AreaLoadForm = reduxForm<Readonly<AreaLoadProps>, FormProps>({
  asyncValidate: asyncValidate(AreaLoadSchema),
  shouldAsyncValidate,
  onChange: (
    values: AreaLoadProps,
    dispatch: Dispatch<Store.Complete>,
    props
  ) => {
    const tmp = parseFloats(values, ['value'])
    dispatch(
      updateLoad(props.loadCaseId, props.loadIndex, {
        name: tmp.name,
        values: [[0, tmp.value]],
        type: tmp.type,
        actsOn: []
      })
    )
  }
})(props => {
  const currentType = R.find(
    opt => opt.value === props.initialValues.type,
    options
  )
  return (
    <div>
      <label htmlFor="type">
        Type
        <Field
          name="type"
          component={CustomSelect}
          disabled={props.auto}
          props={
            {
              options: parseOptions(),
              currentVal: currentType ? currentType.value : ''
            } as SelectProps
          }
        />
      </label>

      <Field
        name="name"
        component={TextInput}
        className="dbs-loads-load-name"
        autoFocus={true}
        disabled={props.auto}
        props={{ title: 'Name' } as FieldConfig}
      />

      <Field
        name="value"
        component={UnitInput}
        disabled={props.auto}
        type="number"
        parse={parseKNm2}
        format={formatKNm2}
        props={unitConfigConversion({
          title: 'Value',
          unit: 'kN/m2'
        } as UnitConfig)}
      />
    </div>
  )
})

type PointLoadProps = {
  name: string
  value: number
  location: number
  type: string
}

type PointLoadFormProps = FormProps &
  InjectedFormProps<Readonly<PointLoadProps>, FormProps>

export function PointLoadSchema(props: PointLoadFormProps) {
  return yup.object().shape({
    name: yup
      .string()
      .min(1)
      .required(),
    type: yup.string().required(),
    value: yup.lazy(() => {
      return yup
        .number()
        .required()
        .min(-500, 'Must be greater than or equal to -' + formatKN(500))
        .max(500, 'Must be less than or equal to ' + formatKN(500))
    }),
    location: yup.lazy(() => {
      return yup
        .number()
        .required()
        .min(0)
        .max(
          props.maxLength || 0,
          'Must be less than or equal to ' + formatMm(props.maxLength || 0)
        )
    })
  })
}

export const PointLoadForm = reduxForm<Readonly<PointLoadProps>, FormProps>({
  asyncValidate: (
    values: PointLoadProps,
    dispatch: Dispatch<Store.Complete>,
    props,
    blurredField: String
  ) => {
    return asyncValidate(PointLoadSchema(props))(
      values,
      dispatch,
      props,
      blurredField
    )
  },
  shouldAsyncValidate,
  onChange: (
    values: PointLoadProps,
    dispatch: Dispatch<Store.Complete>,
    props
  ) => {
    const tmp = parseFloats(values, ['value'])
    dispatch(
      updateLoad(props.loadCaseId, props.loadIndex, {
        name: tmp.name,
        values: [[tmp.location, tmp.value]],
        type: tmp.type,
        actsOn: []
      })
    )
  }
})(props => {
  const currentType = R.find(
    opt => opt.value === props.initialValues.type,
    options
  )
  return (
    <div>
      <label htmlFor="type">
        Type
        <Field
          name="type"
          component={CustomSelect}
          props={
            {
              options: parseOptions(),
              currentVal: currentType ? currentType.value : ''
            } as SelectProps
          }
        />
      </label>

      <Field
        name="name"
        component={TextInput}
        className="dbs-loads-load-name"
        autoFocus={true}
        disabled={props.auto}
        props={{ title: 'Name' } as FieldConfig}
      />

      <Field
        name="value"
        component={UnitInput}
        disabled={props.auto}
        type="number"
        parse={parseKN}
        format={formatKN}
        props={unitConfigConversion({
          title: 'Value',
          unit: 'kN'
        } as UnitConfig)}
      />
      <Field
        name="location"
        component={UnitInput}
        disabled={props.auto}
        type="number"
        parse={parseMm}
        format={formatMm}
        props={unitConfigConversion({
          title: 'Location',
          unit: 'mm'
        } as UnitConfig)}
      />
    </div>
  )
})

type LineLoadProps = {
  name: string
  value: number
  location: number
  value2: number
  location2: number
  type: string
}

type LineLoadFormProps = FormProps &
  InjectedFormProps<Readonly<LineLoadProps>, FormProps>

const ValueSchema = yup.lazy(() => {
  return yup.lazy(() => {
    return yup
      .number()
      .required()
      .min(-500, 'Must be greater than or equal to -' + _formatKNm(500))
      .max(500, 'Must be less than or equal to ' + _formatKNm(500))
  })
})

const LocationSchema = (props: LineLoadFormProps, otherEnd: number) => {
  return yup
    .number()
    .required()
    .min(0)
    .max(
      props.maxLength || 0,
      'Must be less than or equal to ' + formatMm(props.maxLength || 0)
    )
    .test(
      'location',
      "Line locations can't be same",
      location => location !== otherEnd
    )
}

export function LineLoadSchema(
  props: LineLoadFormProps,
  values: LineLoadProps
) {
  return yup.object().shape({
    name: yup
      .string()
      .min(1)
      .required(),
    type: yup.string().required(),
    value: ValueSchema,
    value2: ValueSchema,
    location: LocationSchema(props, values.location2),
    location2: LocationSchema(props, values.location)
  })
}

export const LineLoadForm = reduxForm<Readonly<LineLoadProps>, FormProps>({
  asyncValidate: (
    values: LineLoadProps,
    dispatch: Dispatch<Store.Complete>,
    props,
    blurredField: String
  ) => {
    return asyncValidate(LineLoadSchema(props, values))(
      values,
      dispatch,
      props,
      blurredField
    )
  },
  shouldAsyncValidate,
  onChange: (
    values: LineLoadProps,
    dispatch: Dispatch<Store.Complete>,
    props
  ) => {
    const tmp = parseFloats(values, ['value', 'value2'])
    dispatch(
      updateLoad(props.loadCaseId, props.loadIndex, {
        name: tmp.name,
        values: [[tmp.location, tmp.value], [tmp.location2, tmp.value2]],
        type: tmp.type,
        actsOn: []
      })
    )
  }
})(props => {
  const currentType = R.find(
    opt => opt.value === props.initialValues.type,
    options
  )

  return (
    <div>
      <label htmlFor="type">
        Type
        <Field
          name="type"
          component={CustomSelect}
          disabled={props.auto}
          props={
            {
              options: parseOptions(),
              currentVal: currentType ? currentType.value : ''
            } as SelectProps
          }
        />
      </label>
      <Field
        name="name"
        component={TextInput}
        className="dbs-loads-load-name"
        autoFocus={true}
        disabled={props.auto}
        props={{ title: 'Name' } as FieldConfig}
      />
      {props.autoValue ? (
        <div>
          <label className="read-only-value">
            Value
            <input type="text" disabled={true} value="Automatic" />
          </label>
        </div>
      ) : (
        <div>
          <Field
            name="value"
            component={UnitInput}
            disabled={props.auto}
            type="number"
            parse={_parseKNm}
            format={_formatKNm}
            props={unitConfigConversion({
              title: 'Value',
              unit: 'kN/m'
            } as UnitConfig)}
          />
          <Field
            name="location"
            component={UnitInput}
            disabled={props.auto}
            type="number"
            parse={parseMm}
            format={formatMm}
            props={unitConfigConversion({
              title: 'Location',
              unit: 'mm'
            } as UnitConfig)}
          />
          <Field
            name="value2"
            component={UnitInput}
            disabled={props.auto}
            type="number"
            parse={_parseKNm}
            format={_formatKNm}
            props={unitConfigConversion({
              title: 'Value 2',
              unit: 'kN/m'
            } as UnitConfig)}
          />
          <Field
            name="location2"
            component={UnitInput}
            disabled={props.auto}
            type="number"
            parse={parseMm}
            format={formatMm}
            props={unitConfigConversion({
              title: 'Location 2',
              unit: 'mm'
            } as UnitConfig)}
          />
        </div>
      )}
    </div>
  )
})



// WEBPACK FOOTER //
// ./src/Components/Workspace/Calculation/Editors/LoadTypeEditors.tsx